// CLOSING, MADE LEGIBLE — the instruments that let a human catch somebody in
// orbit without a wiki open on the second monitor.
//
// This file used to teach the hardest lesson on the Brick Moon: that to catch
// a craft AHEAD of you, you must not point at him and burn — you go DOWN, let a
// shorter orbit walk you underneath him, and come back up. That was TRUE, and
// it was measured: on the old 7.2 km moon, pointing and burning to a contact
// 2.5 km ahead cost 481 m/s out of a 420 m/s tank. You could not afford to fly
// at the enemy. So the panel printed the drop, and the pilot flew a transfer.
//
// The moon is fourteen times bigger now and its gravity is fourteen times
// weaker (internal/orbit), and that pair of changes quietly repealed the
// lesson. Circular speed is unchanged, but the PENALTY for leaving the shell
// collapsed: a 600 m climb used to cost 15 m/s of along-track rate and now
// costs 2, while a craft's own engine outweighs the moon thirty to one. The
// same 2.5 km chase, flown the newcomer's way, now costs 89 m/s and takes
// 31 seconds. Meanwhile the drift the old advice rode became glacial — the
// phasing transfer it used to recommend now takes 1008 s of a 720 s round.
//
// The advice and the trap swapped places, so the panel swapped with them.
// Point at him and burn is now the right answer, the arena
// (spaceArenaR) keeps every fight inside a few kilometres so it stays the
// right answer, and orbital mechanics went back to being the scenery instead
// of the toll. What the instrument still owes the pilot is the geometry:
//
//   • CLOSEST APPROACH — where this pass actually ends up, flown forward on
//     the real kernel. The only number that answers "am I going to get there".
//   • CLOSING RATE — signed, so "he is pulling away" is a fact, not a mood.
//   • THE MERGE — the closing rate worth holding and how long it then takes.
//
// The pilot flies it with the lever and the gaze that already fly this ship:
// look at him, hold, watch the range fall, ease off before you arrive. Nothing
// here spends a drop of propellant on its own, and nothing here can be wrong
// about the physics without the probe (tools/check_space_intercept.mjs)
// catching it.

import { stepOrbit, circularVX, wrapDeltaX } from "./orbit_core.js";

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

// The merge law. Closing rate tapers with range (PURSUIT_K per metre) so a
// chase starts brisk and arrives slow enough to shoot from instead of sailing
// straight through; the cap is the most a pilot should ever spend to be
// somewhere, since every m/s is paid twice — once to build and once to kill —
// and 60 m/s of merge is already ~120 m/s of a 420 m/s tank.
const PURSUIT_K = 0.03;
const PURSUIT_MAX_MS = 60;
const PURSUIT_MIN_MS = 18;
// "He's yours" means inside gun range, not merely inside the merge bubble, so
// the clock the panel prints has to run all the way there. The spin-up is the
// second or two the bell spends building the rate in the first place — small,
// constant, and the difference between an ETA that lands and one that flatters.
const GUN_RANGE_M = 700;
const PURSUIT_SPINUP_SEC = 3;

/**
 * How long the merge law takes to walk a range down to gun range: a capped
 * run at the top, then the taper's exponential. Closed form, because an
 * instrument that integrates sixty times a second to print one number is an
 * instrument nobody can check.
 */
function pursuitETA(range) {
  if (range <= GUN_RANGE_M) return 0;
  let t = PURSUIT_SPINUP_SEC;
  let r = range;
  const rCap = PURSUIT_MAX_MS / PURSUIT_K; // above this the rate is flat-out
  if (r > rCap) {
    t += (r - rCap) / PURSUIT_MAX_MS;
    r = rCap;
  }
  // The taper's floor sits inside gun range, so the exponential runs the rest.
  if (r > GUN_RANGE_M) t += Math.log(r / GUN_RANGE_M) / PURSUIT_K;
  return t;
}
// Inside this the geometry is a fight, not a transfer: stop advising drops and
// let the pilot fly the merge.
export const MERGE_RANGE_M = 900;

/**
 * Relative state between two craft on the unrolled cylinder: the wrap-aware
 * offset, the range, and the signed closing rate (positive = the gap is
 * shrinking). Everything else here is built on this.
 */
export function relativeState(me, tgt, wrapM) {
  const dx = wrapDeltaX(tgt.pos.x, me.pos.x, wrapM);
  const dy = tgt.pos.y - me.pos.y;
  const dz = tgt.pos.z - me.pos.z;
  const range = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1e-6;
  const rvx = me.vel.x - tgt.vel.x;
  const rvy = me.vel.y - tgt.vel.y;
  const rvz = me.vel.z - tgt.vel.z;
  const closing = (dx * rvx + dy * rvy + dz * rvz) / range;
  return { dx, dy, dz, range, rvx, rvy, rvz, closing };
}

/**
 * Where this pass actually ends up: propagate BOTH craft on the real kernel and
 * return the closest approach inside the horizon, with when it happens. Coarse
 * steps on purpose — this runs on the instrument's slow tick, and a metre of
 * precision on a three-minute lookahead would be a lie anyway.
 */
export function closestApproach(me, tgt, consts, horizonSec = 180, step = 0.5) {
  const { mu, R, wrapM } = consts;
  const a = { x: me.pos.x, h: me.pos.y, z: me.pos.z, vx: me.vel.x, vh: me.vel.y, vz: me.vel.z };
  const b = { x: tgt.pos.x, h: tgt.pos.y, z: tgt.pos.z, vx: tgt.vel.x, vh: tgt.vel.y, vz: tgt.vel.z };
  let best = Infinity, bestT = 0;
  for (let t = 0; t <= horizonSec; t += step) {
    const dx = wrapDeltaX(b.x, a.x, wrapM);
    const d = Math.sqrt(dx * dx + (b.h - a.h) * (b.h - a.h) + (b.z - a.z) * (b.z - a.z));
    if (d < best) {
      best = d;
      bestT = t;
    }
    stepOrbit(a, mu, R, wrapM, step, 0, 0, 0);
    stepOrbit(b, mu, R, wrapM, step, 0, 0, 0);
  }
  return { missM: best, tSec: bestT };
}

/**
 * The drift rate between two shells, in metres of longitude per second — the
 * engine of every orbital chase, and the number the advice below is built on.
 */
export function driftRate(mu, R, myY, theirY) {
  return Math.abs(circularVX(mu, R, myY) - circularVX(mu, R, theirY));
}

/**
 * THE ADVICE, and it is now the simple one: point at him and burn.
 *
 * `closeMS` is the closing rate to hold, `etaSec` the honest wait at that rate,
 * and `holding` says whether the pilot is already flying it. Returns null
 * inside merge range, where the answer is "you are here, fight".
 *
 * A pursuit still is not free — every metre per second of closing is bought
 * twice, once to open the throttle and once to stop — so the rate is capped at
 * something a tank can pay for across an arena and then some.
 */
export function pursuitAdvice(me, tgt, consts) {
  const { wrapM } = consts;
  const rel = relativeState(me, tgt, wrapM);
  if (rel.range < MERGE_RANGE_M) return null;
  // Scale the closing rate to the gap: a merge from the far side of the arena
  // wants to be brisk, one from just outside gun range wants to be a nudge you
  // can stop out of. The floor keeps a long chase from reading as a standstill.
  const closeMS = clamp(rel.range * PURSUIT_K, PURSUIT_MIN_MS, PURSUIT_MAX_MS);
  return {
    closeMS,
    etaSec: pursuitETA(rel.range),
    holding: rel.closing > closeMS * 0.6,
    opening: rel.closing < -0.5,
    gapM: rel.dx,
    range: rel.range,
  };
}
